import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { MessageSquare, Loader2, Send } from "lucide-react";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { shareComments, shareCommentAdd, policyComments, policyCommentReply } from "../../lib/api";

// Share page: an auditor asks a question on one policy (code-protected link, no vault login).
export const AskOnPolicy = ({ linkId, token, policyId, author }) => {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try { await shareCommentAdd(linkId, token, { policy_id: policyId, text, author }); setText(""); setOpen(false); qc.invalidateQueries({ queryKey: ["share-comments", linkId] }); toast.success("Question sent to the vault owner"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not send"); }
    finally { setBusy(false); }
  };
  return (
    <div className="w-full">
      <button type="button" onClick={() => setOpen((v) => !v)} className="text-[11px] text-primary hover:underline inline-flex items-center gap-1" data-testid={`share-ask-${policyId}`}><MessageSquare className="h-3 w-3" /> {open ? "Cancel" : "Ask a question"}</button>
      {open && (
        <div className="mt-2 flex flex-col sm:flex-row gap-2">
          <Textarea value={text} onChange={(e) => setText(e.target.value)} placeholder="Your question for the vault owner…" className="text-xs min-h-[60px] bg-background" data-testid={`share-ask-text-${policyId}`} />
          <Button size="sm" onClick={send} disabled={busy || text.trim().length < 3} className="rounded-full gap-1 self-end" data-testid={`share-ask-send-${policyId}`}>{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Send</Button>
        </div>
      )}
    </div>
  );
};

export const ShareThread = ({ linkId, token, policyId }) => {
  const { data } = useQuery({ queryKey: ["share-comments", linkId], queryFn: () => shareComments(linkId, token), enabled: !!token, refetchInterval: 30000 });
  const items = (data?.items || []).filter((c) => c.policy_id === policyId);
  if (!items.length) return null;
  return (
    <ul className="w-full mt-2 space-y-1.5" data-testid={`share-thread-${policyId}`}>
      {items.map((c) => (
        <li key={c.id} className="rounded-lg bg-muted/50 px-3 py-2 text-xs">
          <div><b>{c.author}</b> · {c.at.slice(0, 10)}: {c.text}</div>
          {c.reply ? <div className="mt-1 text-primary" data-testid={`share-reply-${c.id}`}><b>{c.replied_by || "Owner"}</b> replied {c.replied_at?.slice(0, 10)}: {c.reply}</div> : <div className="mt-1 text-muted-foreground italic">Awaiting the owner's reply</div>}
        </li>
      ))}
    </ul>
  );
};

// Policies page: owner sees every auditor question and answers inline.
export const OwnerComments = ({ canReply }) => {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["policy-comments"], queryFn: policyComments });
  const [drafts, setDrafts] = useState({});
  const [busy, setBusy] = useState("");
  const reply = async (id) => {
    setBusy(id);
    try { await policyCommentReply(id, drafts[id] || ""); setDrafts((d) => ({ ...d, [id]: "" })); qc.invalidateQueries({ queryKey: ["policy-comments"] }); toast.success("Reply posted — the auditor sees it on their link"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not reply"); }
    finally { setBusy(""); }
  };
  if (!data?.items?.length) return null;
  return (
    <div className="mt-8 rounded-2xl border border-border bg-card p-5" data-testid="policy-comments">
      <div className="flex items-center gap-2 text-sm font-medium"><MessageSquare className="h-4 w-4 text-primary" /> Auditor questions {data.open > 0 && <span className="text-[10px] uppercase tracking-wider font-mono rounded-full px-2 py-0.5 bg-amber-500/10 text-amber-700" data-testid="policy-comments-open">{data.open} open</span>}</div>
      <ul className="mt-3 space-y-3">
        {data.items.map((c) => (
          <li key={c.id} className="rounded-xl border border-border p-3 text-xs" data-testid={`policy-comment-${c.id}`}>
            <div className="text-muted-foreground"><span className="font-mono text-primary">{c.policy_id}</span> · <b className="text-foreground">{c.author}</b>{c.recipient_name && c.recipient_name !== c.author ? ` (${c.recipient_name})` : ""} · {c.at.slice(0, 10)}</div>
            <div className="mt-1 text-sm">{c.text}</div>
            {c.reply ? <div className="mt-2 rounded-lg bg-primary/5 px-3 py-2"><b>{c.replied_by || "You"}</b> · {c.replied_at?.slice(0, 10)}: {c.reply}</div> : canReply ? (
              <div className="mt-2 flex flex-col sm:flex-row gap-2">
                <Textarea value={drafts[c.id] || ""} onChange={(e) => setDrafts((d) => ({ ...d, [c.id]: e.target.value }))} placeholder="Your answer…" className="text-xs min-h-[56px]" data-testid={`policy-comment-reply-text-${c.id}`} />
                <Button size="sm" onClick={() => reply(c.id)} disabled={busy === c.id || !(drafts[c.id] || "").trim()} className="rounded-full self-end gap-1" data-testid={`policy-comment-reply-btn-${c.id}`}>{busy === c.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Reply</Button>
              </div>
            ) : <div className="mt-1 italic text-muted-foreground">Awaiting the owner's reply</div>}
          </li>
        ))}
      </ul>
    </div>
  );
};
